import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { uploadSnapshot } from "@/lib/renderPoster";
import { PosterContent } from "@/components/PosterCanvas";
import { useCategories } from "@/components/categories";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Film, Loader2, Download } from "lucide-react";

const Select = ({ value, onChange, testid, children }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm">{children}</select>
);

const stale = (p) => !p.snapshot_url || (p.snapshot_at || "") < (p.updated_at || "");

export const VideoExportDialog = ({ bannerId, beforeStart }) => {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState("1080");
  const [mode, setMode] = useState("all");
  const [cats, setCats] = useState([]);
  const [screens, setScreens] = useState([]);
  const [job, setJob] = useState(null);
  const [step, setStep] = useState("");
  const [render, setRender] = useState([]);
  const refs = useRef({});
  const { categories } = useCategories();

  useEffect(() => { if (open) api.get("/screens").then(({ data }) => setScreens(data)).catch(() => {}); }, [open]);
  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const t = setInterval(() => api.get(`/video-jobs/${job.id}`).then(({ data }) => setJob(data)).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [job]);
  useEffect(() => { if (job?.status === "error") toast.error(`Échec de la vidéo : ${job.error || ""}`); }, [job?.status, job?.error]);

  const refreshSnapshots = async () => {
    const [{ data: banner }, { data: posters }] = await Promise.all([api.get(`/banners/${bannerId}`), api.get("/posters")]);
    const ids = new Set(banner.images.filter((i) => i.type === "poster").map((i) => i.poster_data?.id));
    const todo = posters.filter((p) => ids.has(p.id) && stale(p));
    if (!todo.length) return;
    setStep(`Mise à jour de ${todo.length} affiche(s)…`);
    setRender(todo);
    await new Promise((r) => setTimeout(r, 1200));
    for (const p of todo) {
      try { await uploadSnapshot(api, p.id, refs.current[p.id], 720, "tv"); }
      catch { toast.warning(`« ${p.name} » : image non mise à jour, l'ancienne version est utilisée`); }
    }
    setRender([]);
  };

  const start = async () => {
    setJob(null);
    try {
      if (beforeStart) { setStep("Enregistrement du bandeau…"); await beforeStart(); }
      await refreshSnapshots();
      setStep("Création de la vidéo…");
      const body = { height: Number(height) };
      if (mode === "cats") body.category_ids = cats;
      else if (mode !== "all") body.ecran = mode;
      const { data } = await api.post(`/banners/${bannerId}/video`, body);
      setJob(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossible de créer la vidéo");
    } finally {
      setStep("");
      setRender([]);
    }
  };

  const running = !!step || job?.status === "running";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" data-testid="video-export-button"><Film className="mr-1 h-4 w-4" /> Vidéo</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="video-export-dialog">
        <DialogHeader>
          <DialogTitle>Télécharger le bandeau en vidéo</DialogTitle>
          <DialogDescription>Fichier MP4 (H.264) d'un tour complet, avec les durées et transitions, à copier sur une clé USB pour une TV non connectée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Qualité</Label>
            <Select value={height} onChange={setHeight} testid="video-quality-select">
              <option value="1080">Full HD 1920×1080</option>
              <option value="720">HD 1280×720 (plus léger)</option>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Contenu</Label>
            <Select value={mode} onChange={setMode} testid="video-content-select">
              <option value="all">Bandeau complet</option>
              {screens.map((s) => <option key={s.id} value={s.id}>{s.name === `Écran ${s.code}` ? s.name : `Écran ${s.code} — ${s.name}`}</option>)}
              <option value="cats">Catégories au choix</option>
            </Select>
          </div>
          {mode === "cats" && <div className="space-y-1.5 rounded-md border border-border p-2" data-testid="video-categories">
            {categories.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={cats.includes(c.id)} data-testid={`video-cat-${c.id}`}
                  onCheckedChange={(v) => setCats((l) => (v ? [...l, c.id] : l.filter((x) => x !== c.id)))} />
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={cats.includes("")} data-testid="video-cat-none"
                onCheckedChange={(v) => setCats((l) => (v ? [...l, ""] : l.filter((x) => x !== "")))} />Images sans catégorie
            </label>
          </div>}
          {running && <div className="space-y-1" data-testid="video-progress">
            <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />{step || `Création de la vidéo (${job.count} images)…`}</p>
            <Progress value={Math.round((job?.progress || 0) * 100)} />
          </div>}
          {job?.status === "done" && (
            <a href={`${API}/video-jobs/${job.id}/download`} download data-testid="video-download-link"
              className="flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              <Download className="h-4 w-4" /> Télécharger la vidéo ({(job.size / 1e6).toFixed(1)} Mo)
            </a>
          )}
          <Button className="w-full" onClick={start} disabled={running || (mode === "cats" && !cats.length)} data-testid="video-start-button">
            {running ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Film className="mr-1 h-4 w-4" />} Créer la vidéo
          </Button>
          <p className="text-[11px] text-muted-foreground">Compter environ 1 minute. Sur la TV : lecteur multimédia de la clé USB, lecture en boucle.</p>
        </div>
        <div aria-hidden style={{ position: "fixed", left: -20000, top: 0 }}>
          {render.map((p) => (
            <div key={p.id} ref={(n) => (refs.current[p.id] = n)} style={{ width: 1280, height: 720 }}><PosterContent data={p} editable={false} /></div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
